import { Link } from 'react-router-dom';
import {
  Alert,
  Badge,
  Button,
  Group,
  Paper,
  Stack,
  Text,
  Title,
} from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import { DataTable, type DataTableColumn } from 'mantine-datatable';
import { format, parseISO } from 'date-fns';
import { POLICY_TYPE_OPTIONS } from '#/service/driver-management/constants/fieldOptions.ts';
import { getLoadErrorMessage } from '#/service/driver-management/utils/apiError.ts';
import { useLatestAccountDocument } from '../hooks/useAccountDocuments.ts';
import { useInsuranceExpiryReminders } from '../hooks/useInsuranceExpiryReminders.ts';
import type { InsuranceExpiryReminder } from '../model.ts';
import { CreateInsuranceModal } from './CreateInsuranceModal.tsx';
import { SectionState } from './SectionState.tsx';

const TITLE_STYLE = { color: 'gray', fontWeight: 'normal' };

const SOURCE_LABEL: Record<string, string> = {
  ACCOUNT_DOCUMENT: '用戶上傳',
  ADMIN: 'ADMIN',
};

const STATE_CONFIG: Record<string, { label: string; color: string }> = {
  ACTIVE: { label: '生效中', color: 'green' },
  INVALIDATED: { label: '已作廢', color: 'gray' },
};

const reviewPath = (documentId: string) =>
  `/driver-management/document-review/${documentId}`;

const columns: DataTableColumn<InsuranceExpiryReminder>[] = [
  {
    title: '險種',
    titleStyle: TITLE_STYLE,
    accessor: 'policyType',
    render: ({ policyType }) =>
      POLICY_TYPE_OPTIONS.find(({ value }) => value === policyType)?.label,
  },
  { title: '車牌號碼', titleStyle: TITLE_STYLE, accessor: 'plateNumber' },
  {
    title: '車險到期日',
    titleStyle: TITLE_STYLE,
    accessor: 'expiryDate',
    render: ({ expiryDate }) => format(parseISO(expiryDate), 'yyyy/MM/dd'),
  },
  {
    title: '保險公司',
    titleStyle: TITLE_STYLE,
    accessor: 'insurer',
    render: ({ insurer }) => insurer ?? '-',
  },
  {
    title: '來源',
    titleStyle: TITLE_STYLE,
    accessor: 'source',
    render: ({ source }) => SOURCE_LABEL[source] ?? source,
  },
  {
    title: '狀態',
    titleStyle: TITLE_STYLE,
    accessor: 'state',
    render: ({ state }) => (
      <Badge
        variant="light"
        size="lg"
        color={STATE_CONFIG[state]?.color ?? 'gray'}
      >
        {STATE_CONFIG[state]?.label ?? state}
      </Badge>
    ),
  },
  {
    title: '操作',
    titleStyle: TITLE_STYLE,
    accessor: 'accountDocumentId',
    render: ({ accountDocumentId }) =>
      accountDocumentId && (
        <Button
          size="xs"
          variant="outline"
          component={Link}
          to={reviewPath(accountDocumentId)}
        >
          查看保單
        </Button>
      ),
  },
];

// 上方提示：最新一筆保單文件為待審核或審核失敗時才顯示
const PolicyDocumentHint = ({ accountId }: { accountId: string }) => {
  const { data: document, error } = useLatestAccountDocument(
    accountId,
    'INSURANCE_POLICY',
  );

  if (error) {
    return (
      <Alert color="red" variant="light">
        <Text size="sm">保單審核狀態：{getLoadErrorMessage(error)}</Text>
      </Alert>
    );
  }

  switch (document?.reviewState) {
    case 'PENDING_REVIEW':
      return (
        <Alert color="blue" variant="light">
          <Group justify="space-between">
            <Text size="sm">用戶已上傳車險保單，尚未審核完成</Text>
            <Button
              size="xs"
              variant="light"
              component={Link}
              to={reviewPath(document.id)}
            >
              審核保單
            </Button>
          </Group>
        </Alert>
      );
    case 'REJECTED':
      return (
        <Alert color="red" variant="light">
          <Group justify="space-between">
            <Text size="sm">
              用戶已上傳車險保單，審核失敗（退件原因：{document.rejectReason}）
            </Text>
            <Button
              size="xs"
              variant="light"
              component={Link}
              to={reviewPath(document.id)}
            >
              查看保單
            </Button>
          </Group>
        </Alert>
      );
    default:
      return null;
  }
};

const InsuranceSection = ({ accountId }: { accountId: string }) => {
  const { data, error, isPending } = useInsuranceExpiryReminders(accountId);
  const [
    isCreateModalOpened,
    { open: openCreateModal, close: closeCreateModal },
  ] = useDisclosure(false);

  const renderTable = () => {
    if (isPending) return <SectionState status="loading" />;
    if (error) return <SectionState status="error" error={error} />;
    if (data.length === 0) {
      return <SectionState status="empty" message="沒有車險保單資料" />;
    }

    return (
      <DataTable
        idAccessor="id"
        verticalSpacing="sm"
        records={data}
        columns={columns}
      />
    );
  };

  return (
    <Paper radius="lg" p="xl" shadow="xs">
      <Group justify="space-between" mb="md">
        <Title order={4}>車險保單</Title>
        <Button variant="outline" onClick={openCreateModal}>
          新增保單
        </Button>
      </Group>
      <Stack gap="md">
        <PolicyDocumentHint accountId={accountId} />
        {renderTable()}
      </Stack>
      <CreateInsuranceModal
        opened={isCreateModalOpened}
        accountId={accountId}
        onClose={closeCreateModal}
      />
    </Paper>
  );
};

export { InsuranceSection };
